import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navigation } from '../App';
import { Heart, Users, BookOpen, Target, ArrowLeft } from 'lucide-react';

export default function AboutPage() {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8 group"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>

        <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-6 text-center">
          About Luck or Skills
        </h1>
        <p className="text-xl text-muted-foreground text-center mb-16">
          A beginner-friendly Texas Hold'em learning community
        </p>

        {/* Mission */}
        <div className="bg-card border border-border rounded-2xl p-8 md:p-12 shadow-xl mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Heart className="w-8 h-8 text-primary" />
            <h2 className="text-2xl font-heading font-bold">Our Mission</h2>
          </div>
          <p className="text-lg text-muted-foreground leading-relaxed mb-4">
            We created Luck or Skills for the benefit of beginning poker players. For just $19.95 per month,
            members can enjoy and communicate with experienced poker players and our expert panel.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed">
            We've got true Texans teaching Texas Hold'em because we believe it is a wonderful social game
            that gets people out of their houses and enjoying fun social interaction.
          </p>
        </div>

        {/* Values */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Link to="/about/community-first" className="bg-card border border-border rounded-xl p-6 shadow-lg hover:border-primary/50 hover:shadow-xl transition-all cursor-pointer">
            <Users className="w-8 h-8 text-primary mb-4" />
            <h3 className="text-xl font-heading font-semibold mb-3">Community First</h3>
            <p className="text-muted-foreground">
              Poker is a social game. We emphasize friendly interaction, mutual respect,
              and helping each other improve.
            </p>
          </Link>
          
          <Link to="/about/education-focused" className="bg-card border border-border rounded-xl p-6 shadow-lg hover:border-primary/50 hover:shadow-xl transition-all cursor-pointer">
            <BookOpen className="w-8 h-8 text-primary mb-4" />
            <h3 className="text-xl font-heading font-semibold mb-3">Education Focused</h3>
            <p className="text-muted-foreground">
              Every livestream, lesson, and discussion is designed to help beginners understand
              and enjoy Texas Hold'em.
            </p>
          </Link>
          
          <Link to="/about/beginner-friendly" className="bg-card border border-border rounded-xl p-6 shadow-lg hover:border-primary/50 hover:shadow-xl transition-all cursor-pointer">
            <Target className="w-8 h-8 text-primary mb-4" />
            <h3 className="text-xl font-heading font-semibold mb-3">Beginner Friendly</h3>
            <p className="text-muted-foreground">
              No question is too basic. We welcome absolute beginners and create a safe
              learning environment.
            </p>
          </Link>
          
          <Link to="/about/texas-poker-culture" className="bg-card border border-border rounded-xl p-6 shadow-lg hover:border-primary/50 hover:shadow-xl transition-all cursor-pointer">
            <div className="text-4xl mb-4">🤠</div>
            <h3 className="text-xl font-heading font-semibold mb-3">Texas Poker Culture</h3>
            <p className="text-muted-foreground">
              Authentic Texas Hold'em taught by real Texans who know the game's social
              and cultural roots.
            </p>
          </Link>
        </div>

        {/* Important Notice */}
        <div className="bg-secondary/50 border border-border rounded-xl p-8 text-center">
          <h3 className="text-xl font-heading font-semibold mb-3">Important Notice</h3>
          <p className="text-muted-foreground">
            <strong>This is NOT a gambling website.</strong> Luck or Skills is an educational and social
            poker community. We teach strategy, host discussions, and build friendships—not gambling.
          </p>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-border text-center">
          <p className="text-muted-foreground text-sm mb-2">© 2024 Luck or Skills - Texas Hold'em Learning Community</p>
          <p className="text-xs text-muted-foreground/40 italic mb-3">"Kiss a Pig" - Patrick Lynch</p>
          <Link 
            to="/admin" 
            className="text-xs text-foreground/30 hover:text-[#C9A227] transition-colors"
            data-testid="footer-admin-link"
          >
            Admin Access
          </Link>
        </div>
      </div>
    </div>
  );
}